import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Form, {MuiRFF, Yup} from '../../form';
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import PartAutocomplete from "../../text-fields/autocomplete/part-autocomplete";
import Grid2 from "@mui/material/Unstable_Grid2";


const formNames = {
    part: 'part',
    amount: 'amount',
};

const UpsertPartRateDialog = ({
                                  open,
                                  close,
                                  onSuccess,
                                  data,
                              }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.upsert-part-rate-dialog'});
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const isEditMode = !!data?.originalPartId;

    const schema = useMemo(() => Yup.object().shape({
        [formNames.part]: Yup.mixed().nullable().required(ValidateMessages.required),
        [formNames.amount]: Yup.number()
            .nullable()
            .required(ValidateMessages.required)
            .typeError(ValidateMessages.incorrectType('number'))
            .min(0, t('rateMinValidation')),
    }), [t]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.part]: data?.originalPartId ? {
            id: String(data.originalPartId),
            partNo: data.partNumber,
        } : null,
        [formNames.amount]: data?.amount ?? null,
    }), [data?.amount, data?.originalPartId, data?.partNumber]);

    /**
     * Resets submitting state when the dialog closes.
     */
    useEffect(() => {
        if (!open)
            setSubmitting(false);
    }, [open]);

    /**
     * Saves the part rate via the API.
     *
     * @type {function(*): Promise<void>}
     */
    const onSubmit = useCallback(async (values) => {
        setSubmitting(true);
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [values[formNames.part]?.partNo],
        })
        if (!isMounted())
            return;
        const partData = partInfoResponse?.data?.[0];
        const forApi = {
            originalPartId: partData?.id,
            partNumber: partData?.partNo,
            parentCategoryId: partData?.parentCategoryId,
            parentCategoryTitle: partData?.parentCategoryTitle,
            amount: Number(values[formNames.amount]),
        };
        const response = await Api.savePartRate(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            onSuccess?.(response.data);
            close?.();
        }
    }, [close, isMounted, onSuccess]);

    return (
        <Dialog open={open} setOpen={close} maxWidth={'sm'} fullWidth>
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
                render={({handleSubmit}) => (
                    <form onSubmit={handleSubmit} noValidate>
                        <DialogTitle>
                            {isEditMode ? t('editTitle') : t('createTitle')}
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid2 container spacing={2} sx={{py: 3}}>
                                <Grid2 xs={12}>
                                    <PartAutocomplete
                                        form
                                        name={formNames.part}
                                        required={required[formNames.part]}
                                        disabled={isEditMode}
                                        textFieldProps={{
                                            label: t('partLabel'),
                                            placeholder: t('partPlaceholder'),
                                        }}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <MuiRFF.TextField
                                        name={formNames.amount}
                                        label={t('rateLabel')}
                                        type={'number'}
                                        required={required[formNames.amount]}
                                        inputProps={{min: 0, step: 'any'}}
                                    />
                                </Grid2>
                            </Grid2>
                        </DialogContent>
                        <DialogActions sx={{display: 'flex', justifyContent: 'space-between', p: 0}}>
                            <Button variant={'dialog'} borderRadius={'0 8px 0 8px'} onClick={close} disabled={submitting}>
                                {t('cancel')}
                            </Button>
                            <Button type={'submit'} variant={'dialog'} borderRadius={'8px 0 8px 0'} disabled={submitting}>
                                {
                                    submitting ? t('saving') : (isEditMode ? t('saveChanges') : t('save'))
                                }
                            </Button>
                        </DialogActions>
                    </form>
                )}
            />
        </Dialog>
    );
};

export default UpsertPartRateDialog;
